import { type Profile, Role, type User } from '@gm-commercial/profile-model';
import type { UserGrantsResponse } from '@gm-commercial/rtk-clients/permissions/types';
import type { ProfileResponse } from '@gm-commercial/rtk-clients/users/types';
import { createSlice, type PayloadAction } from '@reduxjs/toolkit';
import { PURGE } from 'redux-persist';

import {
  API_VERSION_DEFAULTS,
  DEFAULT_API_CONFIG,
} from '~/common/apis/api.constants';
import type { RouteParams } from '~/common/configs/route.config';
import {
  type ApiResponse,
  BDRequestStatus,
  type OperationStatus,
} from '~/common/models/apis/apiResponse.model';
import type { Entity } from '~/common/models/common.model';
import { BDAppErrorType, BDError } from '~/common/models/error.model';
import type {
  ApiOnboardingState,
  OnboardingState,
} from '~/common/models/onboarding.model';
import {
  addAcceptLanguageHeader,
  hasApiResult,
} from '~/common/utils/apis/api.utils';
import {
  makeGetPayloadCreator,
  makePutPayloadCreator,
  makeThunk,
} from '~/common/utils/store/thunk.helper';

import type { RootState } from '../../app/rootReducer';
import { hasPersistedActingOrgContext } from './actingProfile.utils';
import {
  mapApiResponseToOnboardingState,
  mapApiResponseToProfile,
  mapOnboardingStateToApiResponse,
  mapPermissionResponse,
  type ProfileResult,
} from './mappers/profile.mappers';

export enum ProfileRequestType {
  GET_PROFILE = 'Get User Profile',
  GET_PERMISSIONS = 'Get User Permissions',
  GET_ALLOWED_CONTEXTS = 'Get Allowed Contexts',
  GET_ONBOARDING_STATE = 'Get Onboarding State',
  SAVE_ONBOARDING_STATE = 'Save Onboarding State',
}

type ContextParams = Required<Pick<RouteParams, 'organizationsId'>> &
  Pick<RouteParams, 'hubsId' | 'fleetsId'>;

export type ContextItem = Entity & { parentIds?: string[] };

export type AllowedContexts = {
  [orgId: string]: Entity & {
    /** Org's default fleet, sourced from the `/children` response. */
    defaultFleetId?: string;
    hubs: {
      [hubId: string]: Entity & {
        fleets: {
          [fleetId: string]: Entity;
        };
      };
    };
  };
};

type ApiOrgContext = Entity & {
  defaultFleetId?: string;
  hubs: {
    [hubId: string]: Entity & { fleets: { [fleetId: string]: Entity } };
  };
};

// TODO: combine context type predicates when `/children` returns a recursive model
const isOrgContext =
  (params: ContextParams) =>
  (result: unknown): result is ApiOrgContext => {
    return (
      typeof result === 'object' &&
      (result as ApiOrgContext)?.id === params.organizationsId &&
      !!(result as ApiOrgContext)?.hubs
    );
  };

export interface ProfileState {
  profile?: Profile;
  actingProfile?: Profile;
  operations: { [key in ProfileRequestType]?: OperationStatus };
  allowedContexts?: AllowedContexts;
  effectiveContext?: ContextItem;
  selectedContext?: ContextItem; // TODO: ajec: migrate to effectiveContext
  onboardingState?: OnboardingState;
  currentLocale?: string;
  rowsPerPage?: number;
}

const initialState: ProfileState = {
  profile: undefined,
  actingProfile: undefined,
  operations: {},
};

const CONFIG = DEFAULT_API_CONFIG;

export const getOnboardingState = makeThunk(
  'profile/submitOnboardingState',
  makeGetPayloadCreator<ApiResponse<OnboardingState>, void>({
    url: `${globalThis.appConfig.apiBaseUrl}/users/${API_VERSION_DEFAULTS.default}/me/onboardingstate`,
    axiosOptions: (_, state) =>
      addAcceptLanguageHeader(CONFIG, state.profile.currentLocale),
    responseAdapter: (response: unknown | ApiResponse<unknown>) => {
      if (response && hasApiResult<OnboardingState>(response)) {
        return {
          ...response,
          result: mapApiResponseToOnboardingState(response.result),
        };
      }
      throw new BDError('Unexpected onboarding state response', {
        data: response,
      });
    },
  })
);

export const saveOnboardingState = makeThunk(
  'assets/updateAsset',
  makePutPayloadCreator<ApiResponse<ApiOnboardingState>, OnboardingState>({
    url: `${globalThis.appConfig.apiBaseUrl}/users/${API_VERSION_DEFAULTS.default}/me/onboardingstate`,
    axiosOptions: (_, state) =>
      addAcceptLanguageHeader(CONFIG, state.profile.currentLocale),
    argAdapter: (payload) => ({
      requestBody: mapOnboardingStateToApiResponse(payload),
    }),
  })
);

export const getAllowedContexts = makeThunk(
  'profile/getAllowedContexts',
  makeGetPayloadCreator<ApiResponse<AllowedContexts>, ContextParams>({
    url: (_params) =>
      `${globalThis.appConfig.apiBaseUrl}/organizations/${
        API_VERSION_DEFAULTS.default
      }/:organizationsId/children`,
    axiosOptions: (_, state) =>
      addAcceptLanguageHeader(CONFIG, state.profile.currentLocale),
    argAdapter: (params) => ({ requestParams: { ...params } }),
    responseAdapter: (response: unknown, _, params) => {
      const { organizationsId } = params;

      if (hasApiResult<ApiOrgContext>(response, isOrgContext(params))) {
        return {
          ...response,
          result: {
            [organizationsId]: { ...response.result },
          },
        };
      }

      throw new BDError('Unable to parse contexts', { data: response });
    },
  })
);

/**
 * Records that one of the two reads failed. Shared by the thunks and by the
 * `setProfileError` / `setPermissionsError` actions the `UserProvider`
 * dispatches, so consumers that route on these errors (login, secure routes)
 * see the same shape whichever issued the request.
 */
const applyReadError = (
  state: ProfileState,
  request: ProfileRequestType,
  error: BDError
): void => {
  state.operations[request] = {
    status: BDRequestStatus.FAILED,
    errors: [
      {
        type: BDAppErrorType.API,
        ...error,
      },
    ],
  };
};

/**
 * Carries a profile update over to the acting profile when that is only a copy of the user.
 *
 * Outside view-as the acting profile is the user's own profile, taken once at login and persisted,
 * and many screens read it as the current user. A refreshed or patched profile would otherwise
 * leave it behind. A view-as profile is left alone; whether it is one is judged against the updated
 * profile, because a restored session can hold the acting profile before `/me` returns the role.
 */
const syncSelfActingProfile = (state: ProfileState): void => {
  if (
    state.profile?.id &&
    state.actingProfile?.id === state.profile.id &&
    !hasPersistedActingOrgContext(state.profile, state.actingProfile)
  ) {
    state.actingProfile = state.profile;
  }
};

/**
 * Writes a profile response to the slice, marking the read succeeded.
 *
 * Shared by the `getProfile` thunk and the `setProfile` action the
 * `UserProvider` dispatches with the same response, so the store ends up in one
 * shape no matter which of the two loaded the user.
 *
 * Every field on the response is optional, so one that cannot be mapped (no
 * status, for one) is recorded as a failed read rather than thrown: this runs
 * inside a reducer, where an exception would escape the dispatch that produced
 * it and take the app down with it. The previously stored profile is left
 * alone, and login routes off the failed operation as it does for a request
 * that never landed.
 */
const applyProfile = (state: ProfileState, result?: ProfileResponse): void => {
  let profile: Profile;
  try {
    profile = mapApiResponseToProfile(result as ProfileResult);
  } catch (error) {
    applyReadError(
      state,
      ProfileRequestType.GET_PROFILE,
      BDError.asJson(
        error instanceof BDError
          ? error
          : new BDError('Unexpected profile response', {
              type: BDAppErrorType.VALIDATION,
              data: result,
            })
      )
    );
    return;
  }

  state.operations[ProfileRequestType.GET_PROFILE] = {
    status: BDRequestStatus.SUCCEEDED,
  };
  // The permissions live on this same object and are read separately, so
  // `setPermissions` may have already put them here (the responses race, and it
  // holds them on their own until the profile lands). The profile response
  // carries none, so they are carried over rather than dropped — but only for
  // the user they were granted to, which is either this one or none at all when
  // the permissions arrived first.
  const previousPermissions =
    !state.profile?.id || state.profile.id === profile?.id
      ? state.profile?.permissions
      : undefined;
  state.profile = previousPermissions
    ? {
        ...profile,
        permissions: previousPermissions,
        permissionsContextIds: state.profile?.permissionsContextIds,
      }
    : profile;
  if (state.actingProfile?.id && state.actingProfile.id !== profile?.id) {
    state.actingProfile = undefined;
    state.allowedContexts = undefined;
  }
  syncSelfActingProfile(state);
  if (
    (!state.allowedContexts || !Object.keys(state.allowedContexts).length) &&
    profile?.role !== Role.SYSTEM_MANAGER
  ) {
    const organization = profile?.organization;
    state.allowedContexts = organization?.id
      ? {
          [organization.id]: organization as AllowedContexts[string],
        }
      : {};
  }
  if (profile?.userPreferences?.locale) {
    state.currentLocale = profile.userPreferences.locale;
  }
};

export const profileSlice = createSlice({
  name: 'profile',
  initialState,
  reducers: {
    setEffectiveContext: (
      state,
      action: PayloadAction<ContextItem | undefined>
    ) => {
      state.effectiveContext = action.payload;
    },
    setSelectedContext: (
      state,
      action: PayloadAction<ContextItem | undefined>
    ) => {
      state.selectedContext = action.payload;
    },
    patchProfile: (state, action: PayloadAction<Partial<User>>) => {
      if (state.profile) {
        state.profile = {
          ...state.profile,
          ...action.payload,
        };
        syncSelfActingProfile(state);
      }
      if (action.payload?.userPreferences?.locale) {
        state.currentLocale = action.payload?.userPreferences?.locale;
      }
    },
    /**
     * Stores a profile that was read outside `getProfile`, so callers that
     * already hold a response (the `UserProvider` query) do not have to fetch
     * it again.
     */
    setProfile: (state, action: PayloadAction<ProfileResponse>) => {
      applyProfile(state, action.payload);
    },
    /**
     * Stores permissions that were read outside `getPermissions`, so callers
     * that already hold a response do not have to fetch it again.
     *
     * The two reads race, so the permissions may arrive with no profile to
     * merge into. They are held on their own until it does, and `applyProfile`
     * carries them onto the profile that lands after.
     */
    setPermissions: (state, action: PayloadAction<UserGrantsResponse>) => {
      state.operations[ProfileRequestType.GET_PERMISSIONS] = {
        status: BDRequestStatus.SUCCEEDED,
      };
      const permissions = mapPermissionResponse(action.payload);
      state.profile = {
        ...(state.profile ?? {}),
        ...permissions,
      } as Profile;
      syncSelfActingProfile(state);
    },
    /** Records a profile read that failed outside `getProfile`. */
    setProfileError: (state, action: PayloadAction<BDError>) => {
      applyReadError(state, ProfileRequestType.GET_PROFILE, action.payload);
    },
    /** Records a permissions read that failed outside `getPermissions`. */
    setPermissionsError: (state, action: PayloadAction<BDError>) => {
      applyReadError(state, ProfileRequestType.GET_PERMISSIONS, action.payload);
    },
    setActingProfile: (
      state,
      action: PayloadAction<RouteParams | undefined>
    ) => {
      if (state.profile && action.payload) {
        const { organizationsId } = action.payload;
        if (organizationsId) {
          const organization = state.allowedContexts?.[organizationsId];
          if (organization) {
            state.actingProfile = {
              ...state.profile,
              organization,
              permissionsContextIds: {
                ...state.profile.permissionsContextIds,
                organizationsId: organization.id,
              },
              role: Role.ORG_MANAGER,
            };
          }
        }
      } else {
        state.actingProfile = state.profile;
        state.allowedContexts = undefined;
        state.selectedContext = undefined;
      }
    },
    resetProfile: (state) => {
      state.operations[ProfileRequestType.GET_PROFILE] = {
        status: BDRequestStatus.IDLE,
        errors: [],
      };
      state.operations[ProfileRequestType.GET_PERMISSIONS] = {
        status: BDRequestStatus.IDLE,
        errors: [],
      };
      state.profile = undefined;
    },
    setCurrentLocale: (state, action) => {
      //Some browsers like Edge use the list of all prefered languages instead of just the first one
      state.currentLocale = action.payload.split(',')[0];
    },
    setRowsPerPage: (state, action) => {
      state.rowsPerPage = action.payload;
    },
    /**
     * Clears view-as / impersonation data from Redux and redux-persist on logout
     * or session timeout so PII and org context do not remain on shared devices.
     */
    clearPersistedActingContext: (state) => {
      state.actingProfile = undefined;
      state.allowedContexts = undefined;
      state.selectedContext = undefined;
      state.effectiveContext = undefined;
    },
  },
  extraReducers: (builder) => {
    // Onboarding State
    builder.addCase(getOnboardingState.pending, (state) => {
      state.operations[ProfileRequestType.GET_ONBOARDING_STATE] = {
        status: BDRequestStatus.PENDING,
        errors: [],
      };
    });
    builder.addCase(getOnboardingState.fulfilled, (state, action) => {
      state.operations[ProfileRequestType.GET_ONBOARDING_STATE] = {
        status: BDRequestStatus.SUCCEEDED,
      };
      state.onboardingState = action.payload.result;
    });
    builder.addCase(getOnboardingState.rejected, (state, action) => {
      state.operations[ProfileRequestType.GET_ONBOARDING_STATE] = {
        status: BDRequestStatus.FAILED,
        errors: [
          {
            type: BDAppErrorType.API,
            ...(action.payload || (action.error as BDError)),
          },
        ],
      };
    });

    builder.addCase(saveOnboardingState.pending, (state) => {
      state.operations[ProfileRequestType.SAVE_ONBOARDING_STATE] = {
        status: BDRequestStatus.PENDING,
        errors: [],
      };
    });
    builder.addCase(saveOnboardingState.fulfilled, (state, action) => {
      state.operations[ProfileRequestType.SAVE_ONBOARDING_STATE] = {
        status: BDRequestStatus.SUCCEEDED,
      };
      state.onboardingState = action.meta.arg;
    });
    builder.addCase(saveOnboardingState.rejected, (state, action) => {
      state.operations[ProfileRequestType.SAVE_ONBOARDING_STATE] = {
        status: BDRequestStatus.FAILED,
        errors: [
          {
            type: BDAppErrorType.API,
            ...(action.payload || (action.error as BDError)),
          },
        ],
      };
    });

    // allowed contexts
    builder.addCase(getAllowedContexts.pending, (state) => {
      state.operations[ProfileRequestType.GET_ALLOWED_CONTEXTS] = {
        status: BDRequestStatus.PENDING,
        errors: [],
      };
      state.allowedContexts = undefined;
    });
    builder.addCase(getAllowedContexts.fulfilled, (state, action) => {
      state.operations[ProfileRequestType.GET_ALLOWED_CONTEXTS] = {
        status: BDRequestStatus.SUCCEEDED,
      };
      state.allowedContexts = action.payload.result;
    });
    builder.addCase(getAllowedContexts.rejected, (state, action) => {
      state.operations[ProfileRequestType.GET_ALLOWED_CONTEXTS] = {
        status: BDRequestStatus.FAILED,
        errors: [
          {
            type: BDAppErrorType.API,
            ...(action.payload || (action.error as BDError)),
          },
        ],
      };
    });

    // reset state when persist store is purged on logout
    builder.addCase(PURGE, (_state) => {
      _state = initialState;
    });
  },
});

export const {
  setEffectiveContext,
  setSelectedContext,
  patchProfile,
  setProfile,
  setPermissions,
  setProfileError,
  setPermissionsError,
  setActingProfile,
  clearPersistedActingContext,
  resetProfile,
  setCurrentLocale,
  setRowsPerPage,
} = profileSlice.actions;

export const selectProfileState = (state: RootState): RootState['profile'] =>
  state.profile;

export const profileReducer = profileSlice.reducer;
